// src/pages/componenets/sections/footersection/FooterSection1.jsx
import React, { useMemo } from "react";

const replaceSlug = (href, slug) => {
  const s = String(slug || "").trim();
  if (!href) return "";
  return String(href).split("{slug}").join(s);
};

const normalizeHref = (raw, pageSlug = "") => {
  const v0 = (raw || "").trim();
  if (!v0) return "";

  const v = replaceSlug(v0, pageSlug);

  if (v.startsWith("#")) return v;

  const phoneLike = /^\+?\d[\d\s-]{6,}$/.test(v);
  if (phoneLike) {
    const digits = v.replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  const low = v.toLowerCase();
  if (low.startsWith("wa:") || low.startsWith("whatsapp:")) {
    const digits = v.split(":").slice(1).join(":").replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (/^https?:\/\//i.test(v)) return v;
  if (low.startsWith("www.")) return `https://${v}`;
  if (v.startsWith("/")) return v;

  return `/${v}`;
};

const goToLink = (raw, pageSlug = "") => {
  const href = normalizeHref(raw, pageSlug);
  if (!href) return;

  if (href.startsWith("#")) {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }

  const isExternal = /^https?:\/\//i.test(href);
  if (isExternal) window.open(href, "_blank", "noopener,noreferrer");
  else window.location.href = href;
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return { style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` }, className: "" };
    }
  }

  return { style: undefined, className: defaultClass };
};

const iconFor = (type) => {
  const t = String(type || "").toLowerCase();
  switch (t) {
    case "instagram":
      return "📸";
    case "facebook":
      return "📘";
    case "tiktok":
      return "🎵";
    case "youtube":
      return "▶️";
    case "x":
    case "twitter":
      return "✖️";
    case "linkedin":
      return "💼";
    case "telegram":
      return "✈️";
    case "whatsapp":
      return "💬";
    default:
      return "🔗";
  }
};

// alignment is physical: left means left of screen
const pickAlign = (config, direction) => {
  const raw = String(config?.alignment || "").toLowerCase().trim();
  if (raw === "left" || raw === "center" || raw === "right") return raw;
  return direction === "rtl" ? "right" : "left";
};

export default function FooterSection1({ config = {}, style = {}, pageSlug = "" }) {
  // ✅ compute flags first (NO early return before hooks)
  const enabled = config?.enabled !== false;

  const direction = (config?.direction || "ltr").toLowerCase();
  const rtl = direction === "rtl";
  const align = pickAlign(config, direction);

  // ✅ fallback colors (prevents "invisible" footer)
  const titleColor = style?.titleColor || "#ffffff";
  const subtitleColor = style?.subtitleColor || "rgba(255,255,255,0.85)";
  const linkColor = style?.linkColor || "#93C5FD";
  const dividerColor = style?.dividerColor || "rgba(255,255,255,0.15)";

  const textAlignClass =
    align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";

  const justifyRow =
    align === "center"
      ? "justify-center"
      : align === "left"
      ? rtl
        ? "justify-end"
        : "justify-start"
      : rtl
      ? "justify-start"
      : "justify-end";

  const bg = buildBg(style, "bg-gray-900");
  const footerStyle = { ...(bg.style || {}), color: subtitleColor };

  const socials = useMemo(() => {
    const arr = Array.isArray(config.socials) ? config.socials : [];
    return arr
      .map((s) => {
        const type = s?.platform || s?.type || "";
        const url = s?.url || "";
        const label = s?.label || type;
        const id = s?.id || `${type}-${url}`;
        return { id, type, url, label };
      })
      .filter((s) => s.url);
  }, [config.socials]);

  const privacyLink = useMemo(() => {
    if (config.privacyLink) return normalizeHref(config.privacyLink, pageSlug);
    if (pageSlug) return `/${pageSlug}/privacy`;
    return "/privacy";
  }, [config.privacyLink, pageSlug]);

  // Support both old and new location fields
  const locationTitle = config.locationTitle || "Location";
  const locationText = config.locationText || "";
  const addressLines = [
    config.addressLine1,
    config.addressLine2,
    [config.city, config.country].filter(Boolean).join(", "),
  ].filter(Boolean);

  const legalTitle = config.legalTitle || "Legal";

  const developerLink = config.developerWebsite || config.developerLink || "";

  const copyrightLeft =
    config.copyrightLeft || `© ${new Date().getFullYear()} ${config.businessName || "Business"}`;
  const copyrightRight = config.copyrightRight || config.copyrightText || "All rights reserved";

  // ✅ now safe to return after hooks
  if (!enabled) return null;

  return (
    <footer dir={direction} style={footerStyle} className={`px-4 pt-14 pb-10 ${bg.className}`}>
      <div className="container mx-auto max-w-7xl">
        <div className="grid md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className={`md:col-span-5 ${textAlignClass}`}>
            <div className={`flex items-center gap-3 ${justifyRow}`}>
              {config.businessLogo ? (
                <img
                  src={config.businessLogo}
                  alt="Business logo"
                  className="w-12 h-12 rounded-xl object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-white text-xl">
                  🏷️
                </div>
              )}

              <div className="min-w-0">
                <div style={{ color: titleColor }} className="text-xl font-bold truncate">
                  {config.businessName || "Business Name"}
                </div>
                <div className="text-sm opacity-90">
                  {config.businessDescription || "Short business description goes here."}
                </div>
              </div>
            </div>

            {socials.length ? (
              <div className={`mt-6 flex flex-wrap gap-2 ${justifyRow}`}>
                {socials.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => goToLink(s.url, pageSlug)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 transition"
                    title={String(s.type || "social")}
                    type="button"
                  >
                    <span className="text-lg">{iconFor(s.type)}</span>
                    <span className="text-sm">{String(s.label || s.type || "Link")}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* Location */}
          <div className={`md:col-span-4 ${textAlignClass}`}>
            <div style={{ color: titleColor }} className="font-semibold text-base mb-3">
              {locationTitle}
            </div>

            <div className="text-sm space-y-1">
              {locationText ? (
                <div>{locationText}</div>
              ) : (
                addressLines.map((line, idx) => <div key={idx}>{line}</div>)
              )}
              {!locationText && addressLines.length === 0 ? <div>City, Country</div> : null}
            </div>

            <div className="mt-4 space-y-2 text-sm">
              {config.phone ? (
                <div>
                  <span style={{ color: titleColor }} className="font-medium">
                    Phone:
                  </span>{" "}
                  <span dir="ltr" className="inline-block">
                    <a
                      className="underline decoration-white/30 hover:decoration-white"
                      href={`tel:${String(config.phone).replace(/[^\d+]/g, "")}`}
                    >
                      {config.phone}
                    </a>
                  </span>
                </div>
              ) : null}

              {config.email ? (
                <div>
                  <span style={{ color: titleColor }} className="font-medium">
                    Email:
                  </span>{" "}
                  <span dir="ltr" className="inline-block">
                    <a
                      className="underline decoration-white/30 hover:decoration-white"
                      href={`mailto:${config.email}`}
                    >
                      {config.email}
                    </a>
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          {/* Legal + Developer */}
          <div className={`md:col-span-3 ${textAlignClass}`}>
            <div style={{ color: titleColor }} className="font-semibold text-base mb-3">
              {legalTitle}
            </div>

            <button
              type="button"
              onClick={() => goToLink(privacyLink, pageSlug)}
              style={{ color: linkColor }}
              className="text-sm underline decoration-white/20 hover:decoration-white transition"
            >
              {config.privacyLinkText || config.privacyText || "Privacy Policy"}
            </button>

            <div className="mt-8">
              <div style={{ color: titleColor }} className="font-semibold text-base mb-3">
                {config.developedByText || "Developed by"}
              </div>

              <button
                type="button"
                onClick={() => goToLink(developerLink, pageSlug)}
                className={`flex items-center gap-3 p-3 rounded-2xl bg-white/10 hover:bg-white/15 transition w-full ${
                  align === "center" ? "justify-center" : "justify-start"
                }`}
              >
                {config.developerLogo ? (
                  <img
                    src={config.developerLogo}
                    alt="Developer logo"
                    className="w-10 h-10 rounded-xl object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white">
                    ⚡
                  </div>
                )}

                <div className="min-w-0">
                  <div style={{ color: titleColor }} className="text-sm font-semibold truncate">
                    {config.developerName || "Developer Name"}
                  </div>
                  <div className="text-xs opacity-90 truncate" dir="ltr">
                    {developerLink}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-10" style={{ borderTop: `1px solid ${dividerColor}` }} />

        <div
          className={`mt-6 text-xs opacity-90 flex flex-col sm:flex-row flex-wrap gap-2 ${
            align === "center" ? "sm:justify-center" : "sm:justify-between"
          } ${textAlignClass}`}
        >
          <div>{copyrightLeft}</div>
          <div>{copyrightRight}</div>
        </div>
      </div>
    </footer>
  );
}